import { Logo } from "@/components/layout/logo";
import { Button } from "@/components/ui/button";
import {
  FpDropdownMenu,
  FpDropdownMenuContent,
  FpDropdownMenuItem,
  FpDropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FpSidebarTrigger, useSidebar } from "@/components/ui/fp-sidebar";
import { ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { useInputFocus } from "@/hooks";
import { api } from "@/lib/api/api";
import { QUERY_KEYS } from "@/lib/hooks/queryKeys";
import { useActiveChatId } from "@/lib/hooks/useActiveChatId";
import { useIsProcessing } from "@/lib/hooks/useIsProcessing";
import { useMessageStore } from "@/state/stores";
import { DotsVerticalIcon } from "@radix-ui/react-icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { type PropsWithChildren, useEffect, useRef } from "react";
import { ChatContainer } from "./ChatContainer";
import { AttachmentsSection } from "./components";

export function Chat({ children }: PropsWithChildren) {
  const isProcessing = useIsProcessing();
  const isLoading = useMessageStore((state) => state.isLoading);
  const messagesLength = useMessageStore((state) => state.messages.length);
  const chatId = useActiveChatId();
  const queryClient = useQueryClient();
  const router = useRouter();

  const hasVersions = useMessageStore(
    ({ versionInformation: versions }) => Object.keys(versions).length > 0,
  );

  const focusInput = useInputFocus();
  const initialMessageCountRef = useRef(messagesLength);
  const { setOpen } = useSidebar();

  const archiveChatMutation = useMutation({
    mutationFn: (chatId: string) => api.archiveChat(chatId),
    onSuccess: () => {
      // Invalidate and refetch chat list to remove archived chat
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CHATS] });
      // Redirect to codegen page
      router.navigate({ to: "/codegen" });
    },
  });

  const handleArchiveChat = () => {
    if (chatId) {
      archiveChatMutation.mutate(chatId);
    }
  };

  // Focus the textarea when loading and thinking is complete
  useEffect(() => {
    if (!isLoading && !isProcessing) {
      focusInput();
    }
  }, [isLoading, isProcessing, focusInput]);

  // Collapse sidebar when message count increases
  useEffect(() => {
    if (messagesLength > initialMessageCountRef.current) {
      setOpen(false);
      initialMessageCountRef.current = messagesLength;
    }
  }, [messagesLength, setOpen]);

  return (
    <ResizablePanelGroup direction="horizontal" className="h-full">
      <ResizablePanel defaultSize={50}>
        <div className="h-full flex flex-col bg-background relative">
          <header className="border-b shrink-0">
            <div className="flex items-center justify-between h-12 px-4 min-h-12">
              <div className="flex items-center gap-2">
                <Logo />
                <FpSidebarTrigger />
              </div>
              {chatId && (
                <FpDropdownMenu>
                  <FpDropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <DotsVerticalIcon className="h-4 w-4" />
                    </Button>
                  </FpDropdownMenuTrigger>
                  <FpDropdownMenuContent align="end">
                    <FpDropdownMenuItem
                      onClick={handleArchiveChat}
                      disabled={archiveChatMutation.isPending}
                    >
                      {archiveChatMutation.isPending
                        ? "Archiving..."
                        : "Archive Chat"}
                    </FpDropdownMenuItem>
                  </FpDropdownMenuContent>
                </FpDropdownMenu>
              )}
            </div>
          </header>

          <ChatContainer>{children}</ChatContainer>
        </div>
      </ResizablePanel>
      {hasVersions && <AttachmentsSection />}
    </ResizablePanelGroup>
  );
}
